/**
 * FINDING THINGS, AND SAYING TRUTHFULLY HOW MANY THERE ARE.
 *
 * No DOM, no fetch — this takes the indexes it is given and answers questions about them.
 * The awkward part is not the searching, it is the counting: the index carries only the
 * first 250 verses per word per text, so "how many verses contain this" and "how many can
 * I show you" are different numbers, and reporting the second as the first is a false
 * statement about scripture.
 */

import { foldLatin } from '@versesverse/schema/derive';

/** A name on the map, as far as searching and filtering are concerned. */
export interface Searchable {
  id: string;
  name: string;
  /** Which texts it appears in. */
  corpora: string[];
  type?: string;
  /** 'figure' | 'shared' — a shared-passage circle is not a name. */
  kind?: string;
  /** Whether anybody has checked this figure — see `RenderNode.checked`. Absent counts as unchecked. */
  checked?: boolean;
  contested?: boolean;
  crossCorpus?: boolean;
  forms?: { kjv?: string[]; quran?: string[] };
  /** The figure's name in Arabic — `Habil`, `Maryam`. See `matchFigures` for why it is separate
      from `forms.quran`, which is what the Quran SAYS rather than what the figure is called. */
  /* `| null` because that is what `EntityNode` carries — the gazetteer writes an explicit null
     where a figure has no Arabic name, and a narrower type here stops the app assigning its own
     roster to this one. Optional chaining below reads null and undefined identically. */
  arabic?: string | null;
  /**
   * WHO THEY ARE IN A PHRASE — "the first muezzin", "Sasanian emperor of Persia".
   *
   * Searchable because for a great many figures it is the ONLY English there is. A hadith-only
   * Companion has no verse text, no translation and no alias a reader would know; the name is
   * the whole surface, and it is the part a reader is least likely to have. Someone who knows
   * "the first muezzin" and not بِلَال should still find Bilal.
   *
   * `string | null` and not `string | undefined`, because that is what the bundle carries — a
   * figure with no role gets an explicit null. Widening the type to fit the data beats a cast
   * at the one call site, which would only be hiding the same mismatch.
   */
  role?: string | null;
  /**
   * HOW MUCH THE LIBRARY SAYS ABOUT THIS FIGURE — its citation count, and the only prominence
   * signal a search has.
   *
   * Optional because not every caller holds one; absent, everything ties and the ranking falls
   * through to the older tie-breaks. The bundle carries it on every node.
   */
  total?: number;
}

export type Flag =
  | 'people' | 'places' | 'nations'
  | 'verified' | 'shared' | 'contested' | 'parallels'
  | null;

export interface FilterState {
  /** Texts currently switched on. */
  corpora: Set<string> | string[];
  flag?: Flag;
  /** Whether this figure has a cross-scripture parallel — the map knows, the node does not. */
  hasParallel?: (id: string) => boolean;
}

/**
 * Whether a name passes the current filters.
 *
 * Filtering by kind shows only names the texts actually classify. An untyped name is not
 * quietly bundled into one of the three — it drops out, which is the honest answer to
 * "show me the places" when we do not know that it is one.
 */
export function passesFilter(g: Searchable, state: FilterState): boolean {
  const on = state.corpora instanceof Set ? state.corpora : new Set(state.corpora);
  if (!g.corpora.some(c => on.has(c))) return false;

  switch (state.flag) {
    case 'people': return g.type === 'person';
    case 'places': return g.type === 'place';
    case 'nations': return g.type === 'people';
    /* It read `!g.auto` — NOT MACHINE-FOUND — under a chip labelled "verified", so 208 figures
       nobody had checked passed the filter because a person had typed them in. */
    case 'verified': return g.kind === 'shared' || g.checked === true;
    case 'shared': return g.kind === 'figure' && !!g.crossCorpus;
    case 'contested': return !!g.contested;
    case 'parallels': return g.kind === 'shared'
      || (g.kind === 'figure' && !!state.hasParallel && state.hasParallel(g.id));
    default: return true;
  }
}

/**
 * Names matching a term, by their display name or by any spelling the texts use.
 *
 * Searching the spellings matters: a reader who types "Musa" or "Yaqub" is looking for
 * Moses and Jacob, and a name-only search would tell them the scriptures do not mention
 * them.
 */
/*
 * FOLDED THE SAME WAY THE TEXTS ARE, because the reader is copying from one of them.
 *
 * This used plain toLowerCase on both sides while the verse search below folds with foldLatin.
 * The consequence is a reader-facing dead end: the American Standard column on this page spells
 * him Alphæus and the map calls him Alphaeus, so somebody reading that column, typing what they
 * see, is told the scriptures do not mention him. Judæa and Thaddæus the same — 127 ligature
 * spellings exist in the second English column and in no first one.
 *
 * The index has always folded æ to ae, which is why searching VERSES for Alphæus works and
 * searching FIGURES for it did not. One half of the search box did what the data does and the
 * other half did not.
 */
export function matchFigures<T extends Searchable>(nodes: T[], term: string): T[] {
  const t = foldLatin(term.trim());
  if (!t) return [];
  const has = (value: unknown): boolean => foldLatin(String(value ?? '')).includes(t);
  return nodes.filter(n => {
    if (has(n.name)) return true;
    /*
     * AND THE ROLE, because for the hadith figures it is the only English that exists.
     *
     * A Companion has no verse text, no translation and no alias a reader would recognise —
     * the name is the entire searchable surface, and it is the part a reader is least likely
     * to have. "the first muezzin" reaches Bilal; "Persian emperor" reaches Khosrow;
     * "Companion" reaches the eighteen who are one.
     *
     * The NOTE is deliberately not searched. It is prose, several sentences long, and adding
     * it turns a figure-finder into full-text search over commentary — "Medina" would return
     * eighteen figures whose notes mention it rather than the city. The role is one curated
     * phrase that says what someone is, which is the question this box answers.
     */
    if (n.role && has(n.role)) return true;
    /**
     * THE ARABIC PROPER NAME — the field that names the figure, as against the phrases the Quran uses.
     *
     * `forms.quran` is what the QURAN SAYS, which for a figure it does not name is a description:
     * Abel and Cain are "the two sons of Adam" there, Eve is "his wife". Their names in Arabic —
     * Habil, Qabil, Hawwa — live in `arabic`, and until 2026-08-22 nothing here read it.
     *
     * So 39 of the 76 figures carrying one could not be found by it. A reader typing `Maryam`,
     * `Hawwa` or `Asiya` got nothing, while the figure's own page shows exactly that name in its
     * title — `Abel (Habil)` — which is where they would have read it. Aaron worked, and only
     * because `Harun` happens to be in `forms.quran` as well.
     *
     * These are half of this library's audience typing the name they know.
     */
    if (n.arabic && has(n.arabic)) return true;
    const forms = [...(n.forms?.kjv ?? []), ...(n.forms?.quran ?? [])];
    return forms.some(f => has(f));
  });
}

/** word -> verse ids, per text. */
export type Postings = Record<string, Record<string, number[]>>;
/** word -> the TRUE number of verses containing it, where the postings were cut. */
export type DocFreq = Record<string, Record<string, number>>;

export interface VerseHits {
  /** The verse ids the search can actually show. */
  ids: number[];
  /**
   * How many verses really contain this — which may be far more than `ids.length`.
   *
   * For one word this is the recorded document frequency. For several it is the size of
   * the intersection, because intersecting capped lists cannot be extrapolated.
   */
  total: number;
  /** Whether `total` and `ids.length` differ, so the interface can say which is which. */
  capped: boolean;
  /**
   * True when several words were intersected over lists that were themselves cut, so even
   * `total` is a floor. The honest phrasing there is "at least".
   */
  lowerBound: boolean;
}

/**
 * Which verses contain every word, and how many really do.
 *
 * The index holds the first 250 verses per word per text — enough to fill a screen, small
 * enough to download on a phone. The count beside the results must be the real one, or the
 * page reports its own storage limit as a fact about scripture.
 */
export function findVerses(
  words: string[],
  indexes: Postings,
  dfs: DocFreq,
  corpora: string[],
): VerseHits {
  /* Folded to the same spelling the index was built with, so "Judaea" reaches "Judæa".
     One function, in schema/derive.ts, because the last time two places decided what a
     word was they disagreed and the index shipped fragments. */
  const terms = words.map(w => foldLatin(w.trim())).filter(Boolean);
  if (!terms.length) return { ids: [], total: 0, capped: false, lowerBound: false };

  let ids: number[] | null = null;
  for (const w of terms) {
    const set = new Set<number>();
    for (const c of corpora) for (const id of indexes[c]?.[w] ?? []) set.add(id);
    ids = ids ? ids.filter(x => set.has(x)) : [...set];
  }
  const found = ids ?? [];

  const dfOf = (w: string) => corpora.reduce((s, c) => {
    const posting = indexes[c]?.[w];
    if (!posting) return s;
    return s + (dfs[c]?.[w] ?? posting.length);
  }, 0);

  /* a word is "capped" in a text when that text recorded a df — it only does so when it cut */
  const anyCapped = terms.some(w => corpora.some(c => dfs[c]?.[w] !== undefined));
  const total = terms.length === 1 ? (dfOf(terms[0]) || found.length) : found.length;

  return {
    ids: found,
    total,
    capped: total > found.length || (terms.length > 1 && anyCapped),
    lowerBound: terms.length > 1 && anyCapped,
  };
}

/**
 * Which names to light up: those matching by name, plus everyone named in a matching verse.
 *
 * The second half is what makes a word search useful on a map — typing "calf" should show
 * you who is present at the golden calf, not merely that the word occurs.
 */
export function litNames<T extends Searchable>(
  matched: T[],
  verseIds: number[],
  versesOf: (nodeId: string) => number[],
  all: T[],
): Set<string> {
  const out = new Set(matched.map(f => f.id));
  if (!verseIds.length) return out;
  const vs = new Set(verseIds);
  for (const n of all) {
    if (out.has(n.id)) continue;
    if (versesOf(n.id).some(id => vs.has(id))) out.add(n.id);
  }
  return out;
}
